import type { IPatientSchedule } from 'features/Home/interfaces/IInfoSchedule'
import type { JointProvider } from './jointAppointment'
import { jointProvidersOf } from './jointAppointment'

export type JointScheduleEvent = IPatientSchedule & {
  joint_providers?: JointProvider[]
  joint_row_ids?: number[]
}

type MappedRow = IPatientSchedule & { room_id?: string }

const timeKey = (value: unknown): string | number =>
  value instanceof Date ? value.getTime() : String(value)

// Mirrored rows of one visit share the group, the column and the time; null for any other row.
const jointGroupKey = (row: MappedRow): string | null =>
  row.emr_group_id == null
    ? null
    : [row.emr_group_id, row.room_id, timeKey(row.start_time), timeKey(row.end_time)].join('|')

const listedIndex = (row: IPatientSchedule): number =>
  (row.appointment_with ?? []).findIndex(
    (person) => person.consultation_user_id === Number(row.consultation_staff),
  )

// The flagged primary, else the first listed provider, else the lowest id; order of arrival never matters.
const rankRow = (row: IPatientSchedule): [number, number, number] => {
  const index = listedIndex(row)
  return [row.emr_is_primary ? 0 : 1, index < 0 ? Number.MAX_SAFE_INTEGER : index, row.id]
}

const compareRows = (a: IPatientSchedule, b: IPatientSchedule): number => {
  const [ra, rb] = [rankRow(a), rankRow(b)]
  return ra[0] - rb[0] || ra[1] - rb[1] || ra[2] - rb[2]
}

// Only the doctors whose rows sit in this card, named from the visit's list when they're on it.
const providersOf = (rows: IPatientSchedule[], primary: IPatientSchedule): JointProvider[] => {
  const listed = jointProvidersOf(primary)
  return rows.map((row) => {
    const id = Number(row.consultation_staff)
    const known = listed.find((provider) => provider.id === id)
    return {
      id,
      name: known?.name || row.consultation_staff_name || `Provider #${id}`,
      isPrimary: row === primary && primary.emr_is_primary != null,
    }
  })
}

const mergeGroup = (rows: IPatientSchedule[]): JointScheduleEvent => {
  const ranked = [...rows].sort(compareRows)
  const primary = ranked[0]
  return {
    ...primary,
    joint_providers: providersOf(ranked, primary),
    joint_row_ids: ranked.map((row) => row.id).sort((a, b) => a - b),
  }
}

// One card per joint visit: mirrored rows collapse into the primary, in the slot the group first appeared.
export const mergeJointScheduleRows = <T extends MappedRow>(
  events: readonly T[],
): (T & JointScheduleEvent)[] => {
  const groups = events.reduce((map, event) => {
    const key = jointGroupKey(event)
    return key ? map.set(key, [...(map.get(key) ?? []), event]) : map
  }, new Map<string, T[]>())

  return events.flatMap((event) => {
    const key = jointGroupKey(event)
    const group = key ? groups.get(key) : undefined
    if (!group || group.length < 2) return [event]
    if (group[0] !== event) return []
    return [mergeGroup(group) as T & JointScheduleEvent]
  })
}
